import React, { useState, useEffect } from 'react';
import { Box, Paper, CircularProgress, Typography, Stack, Chip } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { CRMEntry, CalendarEvent, CRMStage } from '../../../models/CRMEntry';
import { MeetingDialog } from './MeetingDialog';
import moment from 'moment-timezone';

// Get user's timezone
const userTimezone = moment.tz.guess();

// Define stage colors
const stageColors: Record<CRMStage, string> = {
    'New Lead': '#3498db',
    'Follow-Up Scheduled': '#2ecc71',
    'Proposal Sent': '#f1c40f',
    'Contract Sent': '#e67e22',
    'Active Order': '#9b59b6',
    'Closed Won': '#27ae60',
    'Closed Lost': '#e74c3c'
};

interface EventData {
    id: string;
    title: string;
    start: Date;
    end: Date;
    backgroundColor: string;
    borderColor: string;
    extendedProps: {
        location?: string;
        companyName: string;
        stage: CRMStage;
        entryId: string;
        event: CalendarEvent;
        entry: CRMEntry;
    };
}

export const CRMCalendar: React.FC = () => {
    const [events, setEvents] = useState<EventData[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState<{ event: CalendarEvent; entry: CRMEntry } | null>(null);

    const fetchEvents = async () => {
        try {
            const q = query(collection(db, 'crmEntries'));
            const querySnapshot = await getDocs(q);

            const allEvents: EventData[] = [];
            console.log('Fetched entries:', querySnapshot.size); // Debug log

            querySnapshot.forEach((doc) => {
                const entry = { id: doc.id, ...doc.data() } as CRMEntry;
                console.log('Processing entry:', entry.id, 'Calendar events:', entry.calendarEvents); // Debug log

                if (entry.calendarEvents && Array.isArray(entry.calendarEvents) && entry.calendarEvents.length > 0) {
                    entry.calendarEvents.forEach((event: CalendarEvent) => {
                        console.log('Processing event:', event); // Debug log
                        const stageColor = stageColors[entry.stage] || '#95a5a6';

                        // Convert timestamps to dates and log them
                        const startDate = event.startTime.toDate();
                        const endDate = event.endTime.toDate();
                        console.log('Event dates:', {
                            start: startDate.toISOString(),
                            end: endDate.toISOString()
                        });

                        allEvents.push({
                            id: `${entry.id}-${event.id}`,
                            title: `${entry.companyName} - ${event.title}`,
                            start: startDate,
                            end: endDate,
                            backgroundColor: stageColor,
                            borderColor: stageColor,
                            extendedProps: {
                                location: event.location,
                                companyName: entry.companyName,
                                stage: entry.stage,
                                entryId: entry.id,
                                event: event,
                                entry: entry
                            }
                        });
                    });
                }
            });

            console.log('Final events array:', allEvents); // Debug log
            setEvents(allEvents);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const handleEventClick = (info: any) => {
        const { event, entry } = info.event.extendedProps;
        setSelectedEvent({ event, entry });
    };

    if (loading) {
        return (
            <Box
                sx={{
                    height: 'calc(100vh - 120px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Legend */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack spacing={1}>
                    <Typography variant="subtitle2" gutterBottom>
                        Stage Colors
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                        {Object.entries(stageColors).map(([stage, color]) => (
                            <Chip
                                key={stage}
                                label={stage}
                                sx={{
                                    backgroundColor: color,
                                    color: 'white',
                                    '& .MuiChip-label': {
                                        fontWeight: 500
                                    }
                                }}
                            />
                        ))}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        🌍 Timezone: {moment.tz(userTimezone).format('z')} ({userTimezone})
                    </Typography>
                </Stack>
            </Paper>

            {/* Calendar */}
            <Paper sx={{ p: 2, height: 'calc(100vh - 200px)' }}>
                <Box sx={{ height: '100%' }}>
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin]}
                        initialView="timeGridWeek"
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: 'dayGridMonth,timeGridWeek,timeGridDay'
                        }}
                        events={events}
                        eventContent={eventInfo => {
                            console.log('Rendering event:', eventInfo.event);
                            return (
                                <Box sx={{
                                    p: 0.5,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontSize: '0.85em'
                                }}>
                                    <strong>{eventInfo.event.title}</strong>
                                    {eventInfo.event.extendedProps.location && (
                                        <Box component="span" sx={{ display: 'block', fontSize: '0.85em' }}>
                                            📍 {eventInfo.event.extendedProps.location}
                                        </Box>
                                    )}
                                </Box>
                            );
                        }}
                        eventClick={handleEventClick}
                        views={{
                            timeGridWeek: {
                                slotMinTime: '06:00:00',
                                slotMaxTime: '22:00:00',
                                scrollTime: '08:00:00'
                            },
                            timeGridDay: {
                                slotMinTime: '06:00:00',
                                slotMaxTime: '22:00:00',
                                scrollTime: '08:00:00'
                            }
                        }}
                        allDaySlot={false}
                        slotDuration="00:15:00"
                        expandRows={true}
                        height="100%"
                        stickyHeaderDates={true}
                        nowIndicator={true}
                        timeZone="local"
                        businessHours={{
                            daysOfWeek: [1, 2, 3, 4, 5],
                            startTime: '09:00',
                            endTime: '17:00',
                        }}
                    />
                </Box>
            </Paper>

            {/* Meeting Dialog */}
            {selectedEvent && (
                <MeetingDialog
                    open={true}
                    onClose={() => setSelectedEvent(null)}
                    event={selectedEvent.event}
                    crmEntry={selectedEvent.entry}
                    onEventUpdate={fetchEvents}
                    userTimezone={userTimezone}
                />
            )}
        </Box>
    );
}; 